import { createContext, useContext, useMemo, type ReactNode } from "react";

/**
 * Which widget, if any, the guided demo is currently pointing at.
 *
 * This context exists to keep a dependency from forming. WidgetContainer must
 * not import from src/demo-guide/ — popout windows render WidgetContainer
 * (see ui-components/popout-widgets.tsx), and guide UI must never reach them.
 * So the guide *provides* this and the container *consumes* it, and neither
 * knows the other exists. Same shape as the neighbouring DensityContext.
 *
 * Because the provider is mounted only in the main window, popouts read the
 * default below and never render a ring — which is the behaviour we want
 * anyway: a popped-out widget is already the thing being pointed at.
 *
 * Identity is the widget's grid id ("NewsquawkHeadlines-1"), never a DOM node
 * or a measured position. The dashboard is a drag-resize grid that reflows
 * across five breakpoints; anything coordinate-based would need recalculating
 * on every layout event and would still be wrong mid-drag.
 */
/** A control inside the target widget that the guide is asking them to click. */
export type SpotlightControl = "highlight" | "filter" | "popout" | null;

type WidgetSpotlightValue = {
  /** Grid id of the widget to emphasise, or null when nothing is targeted. */
  targetWidgetId: string | null;
  targetControl: SpotlightControl;
};

const WidgetSpotlightContext = createContext<WidgetSpotlightValue>({
  targetWidgetId: null,
  targetControl: null,
});

/**
 * Set by WidgetContainer for the spotlit widget only, so a control deep in a
 * widget's header can light up without being told which widget it belongs to.
 *
 * Without this, FilterPanelToggle — one shared component rendering the funnel
 * and pen in every widget — would have to thread a grid id down through the
 * panel hooks just to answer "is it me?".
 */
const SpotlightControlContext = createContext<SpotlightControl>(null);

export const SpotlightControlProvider = SpotlightControlContext.Provider;

/** The control the guide wants clicked, if this subtree is the target widget. */
export function useSpotlitControl(): SpotlightControl {
  return useContext(SpotlightControlContext);
}

export function WidgetSpotlightProvider({
  targetWidgetId,
  targetControl = null,
  children,
}: {
  targetWidgetId: string | null;
  targetControl?: SpotlightControl;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({ targetWidgetId, targetControl }),
    [targetWidgetId, targetControl]
  );
  return (
    <WidgetSpotlightContext.Provider value={value}>
      {children}
    </WidgetSpotlightContext.Provider>
  );
}

/** True when this widget is the one the guide is currently pointing at. */
export function useIsSpotlit(widgetId: string | undefined): boolean {
  const { targetWidgetId } = useContext(WidgetSpotlightContext);
  return !!widgetId && targetWidgetId === widgetId;
}

/** The control to emphasise, for the widget that is currently spotlit. */
export function useSpotlightTarget(): WidgetSpotlightValue {
  return useContext(WidgetSpotlightContext);
}
